import { computed, ref } from 'vue'

import { navigateTo } from 'nuxt/app'

import { useInfiniteScroll } from '@common/shared-ui-layer/composables/useInfiniteScroll'

import { useCPNavigation } from '@common/navigation-layer/composables/useNavigationContext'

import { useCourseRedirect } from './useCourseRedirect'
import { useNotification } from './useNotification'
import { resolveCommunitiesNotificationNavigation } from '../utils/communities-notification-redirect.utils'

import { NotificationTypeIds, SourceApp } from '../types/notification.types'

import type { NotificationWebEmit, NotificationWebProps } from '../components/notificationWeb.shared'
import type { INotification } from '../types/notification.types'

export function useNotificationDrawer(props: NotificationWebProps, emit: NotificationWebEmit) {
  const { redirectToCourse } = useCourseRedirect()
  const { CPNavigateTo } = useCPNavigation()
  const { createAllNotificationsInfiniteQuery, createUnreadNotificationsInfiniteQuery, createMarkAsReadMutation } = useNotification()

  const allNotificationsQuery = createAllNotificationsInfiniteQuery()
  const unreadNotificationsQuery = createUnreadNotificationsInfiniteQuery()
  const markAsReadMutation = createMarkAsReadMutation()

  const notificationVisibility = ref<'all' | 'unread'>('all')

  const currentQuery = computed(() => (notificationVisibility.value === 'unread' ? unreadNotificationsQuery : allNotificationsQuery))

  const currentNotifications = computed<INotification[]>(() => currentQuery.value.allData.value || [])

  const isCurrentQueryLoading = computed(() => currentQuery.value.isLoading.value)
  const isCurrentQueryFetchingNextPage = computed(() => currentQuery.value.isFetchingNextPage.value)
  const hasCurrentQueryNextPage = computed(() => currentQuery.value.hasNextPage.value)

  const unReadNotificationCount = computed(() => unreadNotificationsQuery.allData.value.length)

  const hasUnreadNotifications = computed(() => currentNotifications.value.some(notification => !notification.isRead))

  const isMarkAllReadDisabled = computed(() => !hasUnreadNotifications.value || (markAsReadMutation.isPending?.value ?? false))

  const notificationCounterOffset = computed(function () {
    if (unReadNotificationCount.value < 10) {
      return [0, 0] as [string | number, string | number]
    }

    return [2, -2] as [string | number, string | number]
  })

  const { loadMoreTrigger } = useInfiniteScroll(
    function () {
      if (hasCurrentQueryNextPage.value && !isCurrentQueryFetchingNextPage.value) {
        currentQuery.value.fetchNextPage()
      }
    },
    {
      enabled: function () {
        return props.notificationListIsOpen && hasCurrentQueryNextPage.value && !isCurrentQueryFetchingNextPage.value
      },
      rootMargin: '200px',
      threshold: 0.1,
      debounceMs: 0,
    }
  )

  function setNotificationVisibility(value: 'all' | 'unread') {
    notificationVisibility.value = value
  }

  function closeNotificationList() {
    notificationVisibility.value = 'all'
    emit('update:notificationListIsOpen')
  }

  function toggleNotificationList() {
    if (props.notificationListIsOpen) {
      closeNotificationList()
      return
    }

    notificationVisibility.value = 'all'
    emit('update:notificationListIsOpen')
  }

  async function handleMarkAllRead() {
    if (!isMarkAllReadDisabled.value) {
      await markAsReadMutation.mutateAsync({})
    }
  }

  async function handleNotificationItemClick(notification: INotification) {
    try {
      markAsReadMutation.mutateAsync({ notificationId: notification._id })
      closeNotificationList()
      await redirectToUrl(notification)
    } catch (error) {
      console.error('Caught in handleNotificationItemClick', error)
    }
  }

  async function redirectToUrl(notification: INotification) {
    try {
      if (notification.sourceApp === SourceApp.COURSES) {
        await redirectToCourse(
          notification.redirectionUrl,
          notification.type === 'COURSE_POST_COMMENT' && notification.metaData?.commentId
            ? { commentId: notification.metaData.commentId }
            : undefined
        )
        return
      }

      if (notification.type === NotificationTypeIds.CERTIFICATE_ISSUED) {
        await CPNavigateTo({
          name: 'account-settings',
          query: { activeTab: 'certifications' },
        })
        return
      }

      if (notification.sourceApp === SourceApp.EVENTS) {
        await navigateTo(`/${notification.redirectionUrl.replace(/^\/+/, '')}`)
        return
      }

      const communitiesTarget =
        notification.sourceApp === SourceApp.COMMUNITIES ? resolveCommunitiesNotificationNavigation(notification.redirectionUrl) : null

      if (communitiesTarget) {
        await CPNavigateTo(communitiesTarget)
        return
      }

      await navigateTo(`/${notification.redirectionUrl}`)
    } catch (error) {
      console.error('Error redirecting to URL:', error)
    }
  }

  return {
    closeNotificationList,
    currentNotifications,
    handleMarkAllRead,
    handleNotificationItemClick,
    hasUnreadNotifications,
    isCurrentQueryFetchingNextPage,
    isCurrentQueryLoading,
    isMarkAllReadDisabled,
    loadMoreTrigger,
    notificationCounterOffset,
    notificationVisibility,
    setNotificationVisibility,
    toggleNotificationList,
    unReadNotificationCount,
  }
}
